// Required by URL for relative inputs; origin is discarded.
const REDIRECT_PARSE_BASE = 'https://local.invalid'
const REDIRECT_PARSE_ORIGIN = new URL(REDIRECT_PARSE_BASE).origin

function isAllowedRedirectProtocol(parsed: URL): boolean {
  if (parsed.protocol === 'https:') return true
  if (parsed.protocol === 'http:') return process.env.NODE_ENV === 'development'
  return false
}

/**
 * True when `path` stays on this origin if passed to redirect(), router.replace(),
 * or window.location.assign(). Protocol-relative values (`//host/...`) and
 * backslash variants are rejected because browsers treat them as off-site URLs.
 */
function isSameOriginRelativePath(path: string): boolean {
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) {
    return false
  }

  try {
    const verify = new URL(path, REDIRECT_PARSE_BASE)
    return verify.origin === REDIRECT_PARSE_ORIGIN && !verify.username && !verify.password
  } catch {
    return false
  }
}

/**
 * Sanitizes user-supplied redirect targets (e.g. redirectTo query params).
 * Parses with the URL constructor (handles `/path`, `//host/path`, and `https://…`)
 * and returns only the pathname, search, and hash on this site.
 * Protocol-relative results such as `//attacker.example/phish` are rejected.
 * Empty or invalid input returns null (callers choose their own fallback).
 */
export function resolveSafeRedirect(url: string | null | undefined): string | null {
  if (!url?.trim()) return null

  try {
    const parsed = new URL(url.trim(), REDIRECT_PARSE_BASE)
    if (!isAllowedRedirectProtocol(parsed)) return null
    if (parsed.username || parsed.password) return null

    const path = `${parsed.pathname}${parsed.search}${parsed.hash}`
    return isSameOriginRelativePath(path) ? path : null
  } catch {
    return null
  }
}
